<template>
    <EditPanel 
        ref="edit-panel"
        :title="panelTitle"
        :saving="loading"
        @save="editPanelSaveHandler"
        @visibilityChange="panelVisibilityChangeHandler"
    >
        <div class="favourite-panel">
            <div class="form">

                <div class="form__section">
                    <div class="form__fields">
                        <div class="form__field-row" v-if="specialPreset">
                            <Search 
                                placeholder="Adresse suchen" 
                                field-icon="IconSearch" 
                                :standalone="true"
                                :field-index="-1"
                                :value="searchValue"
                                :location-suggestions-only="true"
                                :error="isNameEmpty && submitted"
                                :in-panel="true"
                                @change="searchChangeHandler($event)"
                            />
                        </div>
                        <div v-else-if="data.additionalOption && data.additionalOption !== ''" class="form__field-row favourite-panel__address">
                            <div class="h2 black">{{ specialOptionTitles[data.additionalOption] }}</div>
                            <div v-if="data.title">{{ data.title }}</div>
                        </div>
                        <div v-else-if="favouriteType === 'poi'" class="form__field-row favourite-panel__address">
                            <div class="h2 black">{{ data.name }}</div>
                            <div v-if="data.title">{{ data.title }}</div>
                        </div>
                        <div v-else class="form__field-row">
                            <SmrInput
                                :value="data.name"
                                placeholder="Name"
                                :clearable="true"
                                :maxlength="50"
                                :error="isNameEmpty && submitted"
                                @change="fieldChangeHandler($event, 'name')">
                            </SmrInput>
                        </div>
                        <div class="form__field-row">
                            <SmrInput
                                :value="isRoute ? data.note : data.comment"
                                inputType="textarea"
                                placeholder="Notiz"
                                :maxlength="255"
                                @change="isRoute ? fieldChangeHandler($event, 'note') : fieldChangeHandler($event, 'comment')">
                            </SmrInput>
                        </div>
                    </div>
                </div>


                <div class="form__section" v-if="!isRoute">
                    <div class="form__title">Zur Liste hinzufügen</div>
                    <div class="form__fields">
                        <div class="form__field-row">
                            <SmrChoiceGroup 
                                :values="data.listId" 
                                :options="listOptions"
                                :output-array="true"
                                :single-choice="true"
                                :showInRows="true"
                                :updatableValues="true"
                                @change="fieldChangeHandler($event, 'listId')"
                            />

                            <div class="form__add-item" :class="{ 'move-up' : listOptions.length % 2 !== 0}" @click="newListClickHandler">
                                <span class="form__add-item__text">Neue Liste erstellen</span>
                                <Icon class="form__add-item__icon">
                                    <IconPlusCircle />
                                </Icon>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form__section" v-if="!isRoute">
                    <div class="form__title">Weitere Optionen</div>
                    <div class="form__fields">
                        <div class="form__field-row">
                            <SmrChoiceGroup 
                                v-if="favouritePanelVisible"
                                :values="data.additionalOption" 
                                :options="additionalOptions"
                                :single-choice-checkbox="true"
                                :output-array="false"
                                @change="fieldChangeHandler($event, 'additionalOption')"
                            />
                        </div>
                    </div>
                </div>

                <div class="form__section" v-if="trafficAlertAvailable">
                    <div class="form__fields">
                        <div class="form__field-row">
                            <SmrSwitch 
                                icon="IconBell"
                                label="Traffic Alert"
                                :value="data.trafficAlert"
                                @change="fieldChangeHandler($event, 'trafficAlert')"
                            />
                        </div>
                    </div>
                    <div class="form__description copy-light">Erhalten Sie Benachrichtigungen für aktuelle Verkehrsmeldungen auf dieser Strecke.</div>
                    <div class="form__description" v-if="data.trafficAlert">
                        <span class="copy black">Hinweis: </span>
                        <span class="copy-light">Sie werden nach dem  Speichern auf die Traffic Alert Webseite weitergeleitet, um Ihre Benachrichtigungen (SMS, E-Mail oder App) einrichten zu können.</span>
                    </div>
                </div>

            </div>
        </div>
    </EditPanel>
</template>

<script>
import { mapGetters } from 'vuex'
import { getRouteParams } from '@/utils/route-params'
import { prepareRouteFavouriteOptions } from'@/utils/route-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import eventBus from '@/utils/event-bus';
import isEmpty from 'lodash/isEmpty';
import Search from '@/components/Search.vue'
import EditPanel from '@/components/EditPanel.vue'
import SmrInput from '@/components/form/SmrInput.vue'
import SmrChoiceGroup from '@/components/form/SmrChoiceGroup.vue'
import SmrSwitch from '@/components/form/SmrSwitch.vue'
import Icon from '@/components/Icon.vue'
import IconPlusCircle from '@/components/icons/IconPlusCircle.vue'
import { CUSTOM_LOCATION_TYPES } from '@/data/locationTypes'

export default {
    components: {
        EditPanel,
        Search,
        SmrInput,
        SmrChoiceGroup,
        SmrSwitch,
        Icon,
        IconPlusCircle
    },
    data() {
        return {
            defaultRouteData: {
                name: '',
                note: '',
                origin: {},
                destination: {},
                vias: [],
                trafficAlert: false,
                trafficAlertDetails: false,
                routeSettings: this.activeRouteSettings,
            },
            defaultLocationData: {
                title: '', // saves address
                name: '', // saves name, editable for normal locations
                comment: '',
                objectId: '',
                listId: '',
                lon: 0,
                lat: 0,
                additionalOption: '',
            },
            loading: false,
            submitted: false,
            favouritePanelVisible: false,
            favouriteType: 'ADDRESS', // ADDRESS | poi | route
            specialOptionTitles: {
                work: 'Arbeit',
                home: 'Zuhause'
            },
            data: {},
            favourite: {},
            specialPreset: false, // home | work
            searchValue: '',
            editView: false,
            editWorkHome: false,
        }
    },
    computed: {
        ...mapGetters(['favouritesLists', 'openFavouriteOverlayAfterLogin', 'user', 'activeRouteSettings', 'routeWaypoints', 'selectedPOIs', 'poiSettings', 'poiSettingsChanged']),
        isRoute() {
            return this.favouriteType === 'route'
        },
        panelTitle() {
            if(this.isRoute) {
                if(this.editView) {
                    return 'Route umbenennen'
                } else {
                    return 'Route speichern'
                }
            } else if(this.specialPreset) {
                let title = this.specialOptionTitles[this.specialPreset]
                if(this.editView) {
                    title +=  ' bearbeiten'
                }
                return title
            } else {
                if(this.editView) {
                    return 'Favorit bearbeiten'
                } else {
                    return 'Ort als Favorit speichern'
                }
            }
        },
        isNameEmpty() {
            if(this.isRoute) {
                return this.data.name === ''
            } else {
                if(this.data.name === undefined) return true
                return this.data.name === '' || this.data.name.length > 100
            }
        },
        listOptions() {
            let options = [
                {value: '', label: 'Gemerkte Orte', }
            ]
            this.favouritesLists.forEach(list => {
                options.push({
                    value: list.id,
                    label: list.name
                })
            })
            return options
        },
        additionalOptions() {
            const options = [
                {value: 'home', label: 'Als Zuhause festlegen', },
                {value: 'work', label: 'Als Arbeit festlegen', },
            ]
            if(this.specialPreset) {
                options.forEach(option => option.disabled = true)
            }
            return options
        },
        trafficAlertAvailable() {
            const availableTransportModes = ['CAR', 'CAMPER', 'TRAILER']
            return this.isRoute && availableTransportModes.includes(this.favourite.mode)
        }
    },
    watch: {
        user() {
            // open favourite dialog after logging in (if it's not about home or work)
            if(this.user && !isEmpty(this.openFavouriteOverlayAfterLogin) && !this.openFavouriteOverlayAfterLogin.specialPreset) {
                this.showFavouritesPanelHandler(this.openFavouriteOverlayAfterLogin);
                this.$store.commit('setOpenFavouriteOverlayAfterLogin', {})
            }
        }
    },
    mounted() {
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        showFavouritesPanelHandler(favouriteObject) {
            if(!this.user) {
                this.$store.commit('setOpenFavouriteOverlayAfterLogin', favouriteObject)
                eventBus.$emit('showLoginMenu')
            } else {
                this.favourite = favouriteObject
                this.submitted = false
                this.prepareData()
                this.showDialog()
            }
        },
        panelVisibilityChangeHandler(visible) {
            this.favouritePanelVisible = visible
        },
        editPanelSaveHandler() {
            this.addEditFavourite()
        },
        favouriteAddedEdited() {
            this.loading = false
            this.submitted = false
            this.closeDialog()
        },
        newListClickHandler() {
            eventBus.$emit('showFavouritesListPanel')
        },
        fieldChangeHandler(output, dataKey) {
            this.data[dataKey] = output;
        },
        searchChangeHandler(e) {
            this.favourite = e
            this.searchValue = e.label
            this.setFavouriteType(this.favourite.type)
            this.prepareLocationData(true)
        },
        addListeners() {
            eventBus.$on('showFavouritesPanel', this.showFavouritesPanelHandler);
        },
        removeListeners() {
            eventBus.$off('showFavouritesPanel', this.showFavouritesPanelHandler);
        },
        showDialog() {
            if(this.$refs['edit-panel']) {
                this.$refs['edit-panel'].showDialog();
            }
        },
        closeDialog() {
            this.$refs['edit-panel'].closeDialog();
        },
        prepareData() {
            this.setFavouriteType(this.favourite.poicat ?? this.favourite.type)
            this.resetData()
            this.checkForEditView()

            if(this.isRoute) {
                this.prepareRouteData()
            } else {
                this.prepareLocationPreset()
                this.prepareLocationData()
            }
        },
        checkForEditView() {
            if(this.isRoute) {
                this.editView = this.favourite.id !== undefined || this.favourite.id
            } else {
                this.editView = this.favourite.objectId !== undefined || this.favourite.objectId
            }
            if(this.editView && (this.favourite.id === 'work' || this.favourite.id === 'home')) {
                this.editWorkHome = this.favourite.id
            }
        },
        setFavouriteType(type = '') {
            if(type === 'route') {
                this.favouriteType = 'route'
            } else if((type && type.includes('POI')) || type === 'poi') {
                this.favouriteType = 'poi'
            } else {
                this.favouriteType = 'ADDRESS'
            }
        },
        resetData() {
            this.editView = false
            this.editWorkHome = false
            this.specialPreset = false
            if(this.isRoute) {
                this.data = JSON.parse(JSON.stringify(this.defaultRouteData))
            } else {
                this.data = JSON.parse(JSON.stringify(this.defaultLocationData))
                this.searchValue = ''
            }
        },
        getLocationName(shorterName = true) {
            let fullName = `${this.favourite.name ? this.favourite.name : ''}${this.favourite.comment ? `, ${this.favourite.comment}` : ''}`
            if(shorterName && fullName.length > 50) fullName = this.favourite.name ?? this.favourite.comment
            return fullName
        },
        prepareRouteData() {
            // edit existing route
            if(this.editView) {
                this.data = {...this.favourite}
            } else {
                this.data.name = `${this.routeWaypoints[0].label} - ${this.routeWaypoints[this.routeWaypoints.length - 1].label}`;
                const routeParams = getRouteParams(this.favourite.mode)
                this.data.routeParameters = prepareRouteFavouriteOptions(routeParams)
                this.data.meansOfTransport = [ this.favourite.mode ]
                this.data.trafficAlert = this.favourite.trafficAlert
                this.data.trafficAlertDetails = {
                    duration: this.favourite.duration,
                    distance: this.favourite.distance,
                }
                if(this.favourite.polylines.length <= 1) {
                    this.data.trafficAlertDetails.linestring = {
                        data: this.favourite.polylines[0].polyline
                    }
                } else {
                    this.data.trafficAlertDetails.linestrings = this.favourite.polylines.map(polyline => {
                        return { data: polyline.polyline }
                    })
                }
                // save activated POIs and settings
                if(this.selectedPOIs.length > 0) {
                    this.data.channel = {
                        contextEntries: []
                    }

                    this.selectedPOIs.forEach(poi => {
                        const poiConfig = getPoiConfig(poi)
                        const apiType = poiConfig.apiType
                        this.data.channel.contextEntries.push({
                                id: apiType,
                                source: apiType,
                                enabled: true,
                                query: this.poiSettingsChanged.includes(poi) ? JSON.stringify(this.poiSettings[poi]) : false,
                            })
                    })
                }
                this.routeWaypoints.forEach((waypoint, index) => {
                    const waypointObject = {
                        id: waypoint.id,
                        name: waypoint.label,
                        geoLocation: {
                            lat: waypoint.coordinate.latitude,
                            lon: waypoint.coordinate.longitude
                        },
                        type: waypoint.type && !CUSTOM_LOCATION_TYPES.includes(waypoint.type) ? waypoint.type : 'GEO_ADDRESS',
                    }
                    if(index === 0) {
                        this.data.origin = waypointObject
                    } else if (index === this.routeWaypoints.length - 1) {
                        this.data.destination = waypointObject
                    } else {
                        this.data.vias.push(waypointObject)
                    }
                })
            }
        },
        prepareLocationPreset() {
            // check for specialPreset ( home | work )
            if(this.favourite.specialPreset) {
               this.data.additionalOption = this.favourite.specialPreset
               this.specialPreset = this.favourite.specialPreset
            } else if(this.editWorkHome) {
                this.specialPreset = this.editWorkHome
                this.data.additionalOption = this.favourite.id
            } else {
                this.specialPreset = false
            }
        },
        prepareLocationData(fromSearch = false) {
            if(this.editView && !fromSearch) {
                this.data = {...this.data, ...this.favourite}
                if(this.editWorkHome && this.searchValue === '') {
                    this.searchValue = this.favourite.name
                }
            } else {
                this.data.objectId = this.favourite.locationId ?? this.favourite.id // pin drops have a locationId
                this.data.lat = this.favourite.lat
                this.data.lon = this.favourite.lon

                if(this.favouriteType === 'poi') {
                    this.data.poicat = this.favourite.type.includes('POI_') ? this.favourite.type.split('_')[1] : this.favourite.type
                    this.data.type = this.checkFavouritePoiTypeAllowed(this.data.poicat)
                    const subType = this.getPOISubType(this.data.type)
                    this.data.subType = this.data.poicat === subType ? null : subType
                    if(this.data.subType === false) this.data.subType = null
                    this.data.name = this.favourite.name
                    this.data.title = this.favourite.comment ? this.favourite.comment : this.favourite.address
                    if(this.data.title === false) this.data.title = null
                } else {
                    this.data.name = this.getLocationName()
                    this.data.title = this.favourite.comment ? this.getLocationName(false) : `Koordinaten ${this.favourite.lat}, ${this.favourite.lon}`
                    this.data.type = 'ADDRESS'
                }
            }
        },
        checkFavouritePoiTypeAllowed(poiType) {
            // taken from api docs
            const allowedTypes = ['ACCOMMODATION', 'CAMERA', 'CAMPING', 'EFUEL', 'ENTERTAINMENT', 'FUEL', 'GASTRONOMY', 'GREENZONE', 'HEALTH', 'LEISURE', 'MOBILITY', 'OEAMTC', 'PARKING', 'PASSROAD', 'PUBLICTRANSPORT', 'ROADHOUSE', 'SHOPPING', 'SHOTTERMPARKING', 'SIGHT', 'SPECIALTOLLROAD', 'SPORTS', 'TUNNEL']
            if(allowedTypes.includes(poiType)) return poiType
            return null
        },
        getPOISubType(type) {
            if(type === 'FUEL') {
                return null
                // TODO: use as soon as we can save fuel subtypes
                // return getCategoryLabelByValue('gasStations', this.favourite.category)
            } 

            if (this.favourite.category === 'OECC') {
                return 'CAMPING'
            }

            if(type === 'EFUEL') {
                return null
            }

            return this.favourite.category
        },
        async addEditFavourite() {
            this.submitted = true
            if(!this.isNameEmpty) {
                this.loading = true
                if(this.isRoute) {
                    if(this.editView) {
                        await this.$store.dispatch('editSavedRoute', this.data)
                    } else {
                        await this.$store.dispatch('addSavedRoute', this.data)
                        eventBus.$emit('activeRouteSaved')
                    }
                } else {
                    if(this.editView) {
                        await this.$store.dispatch('editFavourite', this.data)
                    } else {
                        await this.$store.dispatch('addFavourite', this.data)
                    }
                }
                this.favouriteAddedEdited()
            }
        },
    }
}
</script>

<style lang="scss">
.favourite-panel {
    &__input {
        margin-bottom: 6px;
    }

    &__address {
        margin-bottom: 23px !important;
    }
}
</style>